import { createAsyncThunk } from "@reduxjs/toolkit";
import type {
  ReportSchedulePayload,
  ReportScheduleResponse,
  DownloadReportPayload,
  DownloadOldReportPayload,
  ReportTemplate
} from "../types/reportTypes";
import { axiosInstance } from "../../utils/common";
import type { RootState } from "../../store";

export const createReportSchedule = createAsyncThunk<
  ReportScheduleResponse,
  ReportSchedulePayload,
  { rejectValue: string }
>("report/createSchedule", async (payload, thunkAPI) => {
  try {
    const response = await axiosInstance.post<ReportScheduleResponse>(
      `reports/report_schedules/`,
      payload
    );
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});
// reportService.ts
export const updateReportSchedule = createAsyncThunk(
  "report/updateSchedule",
  async (
    { id, payload }: { id: number; payload: Partial<ReportSchedulePayload> },
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.patch(
        `reports/report_schedules/${id}/`,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchReportTemplates = createAsyncThunk<
  ReportTemplate[], // Type of the fulfilled action payload
  void, // No argument is passed
  { rejectValue: string } // Type of the rejected action payload
>("report/fetchTemplates", async (_, thunkAPI) => {
  try {
    const response = await axiosInstance.get<ReportTemplate[]>(
      `reports/listCarboneTemplates/`
    );
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const fetchReportSchedules = createAsyncThunk<
  ReportScheduleResponse[],
  { recurrence?: string; mainTabKey: string }, // << accept optional {recurrence} argument
  { rejectValue: string; state: RootState }
>("report/fetchSchedules", async (filters, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const customer = state.userInfo.selectedCustomerName;
    let url = `reports/fetch_report_schedules/?created_by_customer=${customer}&is_periodic=`;

    if (filters.mainTabKey === "single") {
      url += "false";
    } else {
      url += "true";
      if (filters.recurrence) {
        url += `&recurrence=${filters.recurrence}`;
      }
    }
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const updateReportScheduleStatus = createAsyncThunk(
  'report/updateScheduleStatus',
  async ({ id, is_active }: { id: number, is_active: boolean }, thunkAPI) => {
    try {
      const response = await axiosInstance.patch(
        `reports/updateScheduleStatus/${id}/`,
        { is_active }
      );
      return response.data.data; // Return the updated data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchReportLogs = createAsyncThunk(
  "report/fetchLogs",
  async (report_schedule_id: number, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        `reports/report_logs/?report_schedule=${report_schedule_id}`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
// reportService.ts

export const downloadReport = createAsyncThunk<
  void,
  DownloadReportPayload,
  { rejectValue: string }
>("report/downloadReport", async ({ id, report_type, file_type }, thunkAPI) => {
  try {
    const response = await axiosInstance.get(
      `reports/download_report/?report_schedule_id=${id}`,
      {
        params: { report_type, file_type: file_type },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${report_type}.${file_type}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return;
  } catch (error: any) {
    try {
      const errorBlob = error.response?.data;
      const text = await errorBlob.text();
      const data = JSON.parse(text);
      const errorMessage =
        data?.error || (typeof data === "string" ? data : "Download failed.");
      return thunkAPI.rejectWithValue(errorMessage);
    } catch {
      return thunkAPI.rejectWithValue("Download failed.");
    }
  }
});

export const downloadOldReport = createAsyncThunk<
  void,
  DownloadOldReportPayload,
  { rejectValue: string }
>(
  "report/downloadReportFromLog",
  async ({ report_log_id, file_type, fileName }, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        `reports/downloadReportFromLog/`,
        {
          params: {
            report_log_id,
            file_type,
          },
        }
      );

      const { fileContent, fileType, error } = response.data;

      if (error) {
        return thunkAPI.rejectWithValue(error);
      }

      // Base64 decoding logic
      const byteCharacters = atob(fileContent);
      const byteArrays: Uint8Array[] = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
        const slice = byteCharacters.slice(offset, offset + 1024);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }

      const blob = new Blob(byteArrays as BlobPart[], { type: `application/${fileType}` });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return;
    } catch (error: any) {
      return thunkAPI.rejectWithValue("Download failed.");
    }
  }
);

export const triggerScheduledReport = createAsyncThunk<
  void, // Return type of the thunk
  { schedule_id: string }, // Payload argument type
  { rejectValue: string } // Rejected value type
>("report/triggerReportNow", async ({ schedule_id }, thunkAPI) => {
  try {
    const response = await axiosInstance.post(
      `reports/triggerReportNow/${schedule_id}/`
    );

    // Optional: log or toast success response if needed
    return;
  } catch (error: any) {
    console.error("Trigger failed", error);

    const errorMessage =
      error?.response?.data?.message || "Failed to trigger report.";
    return thunkAPI.rejectWithValue(errorMessage);
  }
});

export const deleteReportSchedule = createAsyncThunk(
  "report/deleteSchedule",
  async (schedule_id: string, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(
        `reports/delete_report/${schedule_id}/`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const archiveReportSchedule = createAsyncThunk(
  "report/archiveSchedule",
  async (schedule_id: string, thunkAPI) => {
    try {
      const response = await axiosInstance.patch(
        `reports/archive_report/${schedule_id}/`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);


